/**
 * Сегментированный переключатель (§2.3, анатомия <ChannelSwitch>).
 *
 * Радиогруппа, а не набор кнопок: выбран ровно один вариант, и скринридер
 * должен это слышать. Отсюда `role="radiogroup"` / `role="radio"` и ровно один
 * сегмент в tab-порядке.
 *
 * Клавиатура ровно по спеке: «←/→ внутри сегмента, Enter — применить». То есть
 * стрелки **переносят фокус, но не переключают**: смена канала перестраивает
 * весь экран, и делать это попутным нажатием стрелки нельзя. Это допустимый
 * вариант радиогруппы — без «выбор следует за фокусом».
 */
import { useRef, useState } from 'react'
import './SegmentedControl.css'

export interface SegmentedOption<T extends string> {
  value: T
  label: string
  disabled?: boolean
}

export interface SegmentedControlProps<T extends string> {
  value: T
  options: SegmentedOption<T>[]
  onChange: (value: T) => void
  /** Доступное имя группы: «Канал», «Плотность». */
  label: string
  size?: 'm' | 's'
  /** Фиксированная ширина сегмента в px: у канала это 92 (§2.3). */
  segmentWidth?: number
  className?: string
}

export function SegmentedControl<T extends string>({
  value,
  options,
  onChange,
  label,
  size = 'm',
  segmentWidth,
  className,
}: SegmentedControlProps<T>) {
  const itemsRef = useRef<(HTMLButtonElement | null)[]>([])
  const selectedIndex = options.findIndex((option) => option.value === value)
  /** Куда уехал фокус стрелками. null — фокус на выбранном сегменте. */
  const [focusedIndex, setFocusedIndex] = useState<number | null>(null)

  const tabStop = focusedIndex ?? (selectedIndex >= 0 ? selectedIndex : 0)

  const classes = ['segmented']
  if (size === 's') classes.push('segmented--s')
  if (className) classes.push(className)

  /** Следующий доступный сегмент; на краях остаёмся на месте. */
  function move(from: number, direction: 1 | -1) {
    let index = from
    for (let step = 0; step < options.length; step += 1) {
      index += direction
      if (index < 0 || index > options.length - 1) return
      if (!options[index]?.disabled) {
        setFocusedIndex(index)
        itemsRef.current[index]?.focus()
        return
      }
    }
  }

  return (
    <div
      className={classes.join(' ')}
      role="radiogroup"
      aria-label={label}
      /* Фокус ушёл из группы целиком — tab-стопом снова становится выбранный
         сегмент. Внутренние переходы между сегментами это не сбрасывает. */
      onBlur={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget)) setFocusedIndex(null)
      }}
    >
      {options.map((option, index) => {
        const checked = option.value === value
        return (
          <button
            key={option.value}
            ref={(node) => {
              itemsRef.current[index] = node
            }}
            type="button"
            role="radio"
            aria-checked={checked}
            aria-disabled={option.disabled || undefined}
            tabIndex={index === tabStop ? 0 : -1}
            className="segmented__item t-btn"
            style={segmentWidth != null ? { width: `${segmentWidth}px` } : undefined}
            onClick={() => {
              if (option.disabled) return
              setFocusedIndex(null)
              onChange(option.value)
            }}
            onKeyDown={(event) => {
              if (event.key === 'ArrowRight' || event.key === 'ArrowDown') {
                event.preventDefault()
                move(index, 1)
              } else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') {
                event.preventDefault()
                move(index, -1)
              }
              /* Enter и Space обрабатывает сам браузер: он превращает их в click. */
            }}
          >
            {option.label}
          </button>
        )
      })}
    </div>
  )
}
